import React, { useEffect, useState } from 'react';
import PopUpNewVersion from './components/PopUpNewVersion';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features, { MarqueeBar } from './components/Features';
import Comparison from './components/Comparison';
import StackSection from './components/StackSection';
import Plans from './components/Plans';
import Footer from './components/Footer';
import SeoHead from './components/SeoHead';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Legal from './pages/Legal';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import Terms from './pages/Terms';
import About from './pages/About';
import Status from './pages/Status';
import NotFound from './pages/NotFound';
import { FiArrowUpCircle } from 'react-icons/fi';

function useVersionInfo() {
  const [versionInfo, setVersionInfo] = useState(null);
  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then(setVersionInfo)
      .catch(() => setVersionInfo(null));
  }, []);
  return versionInfo;
}

// Force dark mode by default on first load
if (typeof window !== 'undefined') {
  const theme = localStorage.getItem('pulsecio_theme');
  if (!theme) {
    document.documentElement.classList.add('dark');
  }
}

function HomePage() {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const timeout = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 relative">
      <SeoHead
        title="PulseCIO — SaaS Communication IT, Tâches & Gouvernance | DSI, PME, Associations"
        description="PulseCIO unifie messagerie structurée, gestion de tâches, gouvernance IT et conformité RGPD en un seul SaaS. Alternative à Notion, ClickUp et Confluence. Multi-tenant, chiffrement AES-256. DSI, PME, associations. Essai gratuit dès 0€."
        ogTitle="PulseCIO — SaaS Communication IT & Gouvernance | Alternative Notion, ClickUp, Confluence"
        ogDescription="Messagerie structurée, gestion de tâches, gouvernance IT et conformité RGPD — tout-en-un. Idéal pour DSI, PME et associations. Remplacez 4 outils par un seul. Gratuit."
        ogImage="/images/og/pulsecio-cover.png"
        ogUrl="https://pulsecio.com"
        twitterCard="summary_large_image"
      />
      <Navbar />
      <main className="pt-20">
        <Hero />
        <MarqueeBar />
        <div className="container mx-auto px-6">
          <Features />
          <StackSection />
        </div>
        <Comparison />
        <Testimonials />
        <div className="container mx-auto px-6">
          <Plans />
          <FAQ />
        </div>
      </main>
      <Footer />
      <BackToTopButton />
    </div>
  );
}

// Composant bouton retour en haut (DA cohérente, centré en haut, couleur adaptative)
function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          if (currentY < lastScrollY && currentY > 200) {
            setVisible(true);
          } else {
            setVisible(false);
          }
          lastScrollY = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function handleClick(e) {
    e.preventDefault();
    // Scroll smooth de 2 secondes avec Lenis si dispo, sans stop ni manipulation d'état
    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      window.lenis.scrollTo(0, { immediate: false, duration: 2 });
    } else {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }
  }

  return (
    <button
      onClick={handleClick}
      className={`fixed top-32 left-1/2 -translate-x-1/2 z-50 px-5 py-2 rounded-full font-semibold border border-gray-300 transition duration-300
        ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        bg-gray-200/90 text-gray-900 dark:bg-white/90 dark:text-gray-900 hover:bg-gray-300 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary`}
      aria-label="Retour en haut"
      style={{backdropFilter:'blur(6px)', boxShadow:'0 8px 32px 0 rgba(31, 38, 135, 0.25), 0 1.5px 6px 0 rgba(0,0,0,0.10)'}}
    >
      <FiArrowUpCircle className="inline-block h-5 w-5 mr-1 align-text-bottom" /> Retour en haut
    </button>
  );
}

function App() {
  const versionInfo = useVersionInfo();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!versionInfo) return;
    const lastClosed = localStorage.getItem('popupNewVersionClosed');
    if (lastClosed === versionInfo.version) {
      setShowPopup(false);
    } else {
      setShowPopup(true);
    }
  }, [versionInfo]);

  const handleClosePopup = () => {
    if (versionInfo) {
      localStorage.setItem('popupNewVersionClosed', versionInfo.version);
    }
    setShowPopup(false);
  };

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/status" element={<Status />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        {versionInfo && showPopup && (
          <PopUpNewVersion
            version={versionInfo.version}
            date={versionInfo.date}
            changes={versionInfo.changes}
            onClose={handleClosePopup}
          />
        )}
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;